import { api, type ApiSchema } from "@/modules/api";
import { localStorageModule } from "@/modules/local-storage";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import type { ProactivePaywallSource } from "@/domains/metrics";
import type { AppStore } from "@/store";
import type { INoteObservable, NoteReminderShill } from "@/store/note";
import type { LocalStorageReminderShillUpsellAttributionContext } from "@/modules/local-storage/types";

export type ReminderShillUpsellAttributionContext =
  LocalStorageReminderShillUpsellAttributionContext;

/**
 * Builds the durable reminder-shill attribution payload from the generated note context.
 */
export const buildReminderShillUpsellAttributionContext = ({
  noteObservable,
  reminderShill,
  source,
}: {
  noteObservable: Pick<INoteObservable, "id">;
  reminderShill: Pick<NoteReminderShill, "id" | "text">;
  source: ProactivePaywallSource;
}): ReminderShillUpsellAttributionContext => ({
  noteId: noteObservable.id,
  reminderShillId: reminderShill.id,
  generatedReminderText: reminderShill.text,
  source,
});

/**
 * Stores the attribution context before checkout leaves the app.
 *
 * Passing no context intentionally clears any older record so a later checkout
 * cannot inherit stale reminder-shill attribution from the same browser session.
 */
export const writePendingReminderShillUpsellAttribution = (
  context: ReminderShillUpsellAttributionContext | null | undefined
): void => {
  localStorageModule.writePendingReminderShillUpsellAttribution(context);
};

/**
 * Reads the pending checkout attribution, removing stale or malformed records.
 */
export const readPendingReminderShillUpsellAttribution =
  (): ReminderShillUpsellAttributionContext | null => {
    return localStorageModule.readPendingReminderShillUpsellAttribution();
  };

/**
 * Removes pending reminder-shill checkout attribution for canceled or failed flows.
 */
export const clearPendingReminderShillUpsellAttribution = (): void => {
  localStorageModule.clearPendingReminderShillUpsellAttribution();
};

/**
 * Hides the reminder shill associated with a successful checkout attribution.
 */
export async function dismissReminderShillUpsellAttributionContext({
  context,
  store,
}: {
  context: ReminderShillUpsellAttributionContext | null | undefined;
  store: AppStore;
}): Promise<void> {
  if (!context) {
    return;
  }

  try {
    let note = await store.notes.getAsync(context.noteId);

    if (!note?.reminderShill) {
      const didFetchNote = await store.notes.fetch(context.noteId);

      if (didFetchNote) {
        note = await store.notes.getAsync(context.noteId);
      }
    }

    const reminderShill = note?.reminderShill;

    if (!note || reminderShill?.id !== context.reminderShillId || reminderShill.is_dismissed) {
      return;
    }

    await note.dismissReminderShill();
  } catch (unknownError) {
    const error = unknownError as Error;

    logger.warn({
      message: "[dismissReminderShillUpsellAttributionContext] Failed to dismiss reminder shill",
      info: { error: objectModule.safeErrorAsJson(error) },
    });
  }
}

/**
 * Hides the reminder shill that started checkout once Proactive is confirmed.
 */
export async function dismissPendingReminderShillUpsellAttribution({
  store,
}: {
  store: AppStore;
}): Promise<void> {
  await dismissReminderShillUpsellAttributionContext({
    context: readPendingReminderShillUpsellAttribution(),
    store,
  });
}

/**
 * Persists a reminder-shill attribution record to the backend.
 */
export async function recordReminderShillUpsellAttribution({
  context,
  stripeCheckoutSessionId,
}: {
  context: ReminderShillUpsellAttributionContext;
  stripeCheckoutSessionId?: string | null;
}): Promise<boolean> {
  try {
    const body = {
      note_id: context.noteId,
      reminder_shill_id: context.reminderShillId,
      generated_reminder_text: context.generatedReminderText,
      source: context.source,
      stripe_checkout_session_id: stripeCheckoutSessionId ?? null,
    } satisfies ApiSchema["RecordReminderShillUpsellAttributionRequestSchema"];
    const response = await api.post("/v2/me/platform-info/reminder-shill-upsell-attribution", {
      body,
    });

    return response.data?.success === true;
  } catch (unknownError) {
    const error = unknownError as Error;

    logger.warn({
      message: "[recordReminderShillUpsellAttribution] Failed to persist attribution",
      info: { error: objectModule.safeErrorAsJson(error) },
    });

    return false;
  }
}

/**
 * Records the pending checkout attribution after Proactive checkout is confirmed.
 */
export async function recordPendingReminderShillUpsellAttribution({
  stripeCheckoutSessionId,
}: {
  stripeCheckoutSessionId?: string | null;
}): Promise<boolean> {
  const context = readPendingReminderShillUpsellAttribution();

  if (!context) return false;

  const didRecord = await recordReminderShillUpsellAttribution({
    context,
    stripeCheckoutSessionId,
  });

  if (didRecord) {
    clearPendingReminderShillUpsellAttribution();
  }

  return didRecord;
}
